/**
 * Handles fixed positioning behavior for filter navigation.
 *
 * Two visual states:
 *   1. STATIC – element sits in normal document flow.
 *   2. FIXED  – element is pinned to the viewport.
 */
export class StickyBehavior {
    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            breakpoint: 1200,
            collapsedClass: 'is-collapsed',
            stickyOffset: 0.5,
            stickyAdjustment: 40,
            resizeDebounce: 150,
            ...options,
        };

        this.isFixed = false;
        this.rafId = null;
        this.resizeTimeout = null;
        this.originalTop = null;
        this.placeholder = null;
        this.fixedTopPx = 0;

        this.init();
    }

    init() {
        this.createPlaceholder();
        this.cacheOriginalPosition();
        this.fixedTopPx = this.getFixedTopPx();

        this.checkResponsive();
        this.update();

        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);

        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize);
    }

    /* ------------------------------------------------------------------ */
    /*  Placeholder                                                        */
    /* ------------------------------------------------------------------ */

    createPlaceholder() {
        this.placeholder = document.createElement('div');
        this.placeholder.style.display = 'none';
        this.placeholder.setAttribute('aria-hidden', 'true');
        this.element.parentNode.insertBefore(this.placeholder, this.element);
    }

    showPlaceholder() {
        const rect = this.element.getBoundingClientRect();
        this.placeholder.style.height = `${rect.height}px`;
        this.placeholder.style.display = 'block';
    }

    hidePlaceholder() {
        this.placeholder.style.display = 'none';
    }

    /* ------------------------------------------------------------------ */
    /*  Position helpers                                                    */
    /* ------------------------------------------------------------------ */

    cacheOriginalPosition() {
        const wasFixed = this.isFixed;

        if (wasFixed) {
            this.applyStatic();
        }

        const rect = this.element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        this.originalTop = rect.top + scrollTop;

        if (wasFixed) {
            this.applyFixed();
        }
    }

    getFixedTopPx() {
        const vh = window.innerHeight || document.documentElement.clientHeight;

        return vh * this.options.stickyOffset - this.options.stickyAdjustment;
    }

    isStuck() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        return scrollTop + this.fixedTopPx >= this.originalTop;
    }

    /* ------------------------------------------------------------------ */
    /*  State transitions                                                  */
    /* ------------------------------------------------------------------ */

    applyStatic() {
        this.element.style.position = '';
        this.element.style.top = '';
        this.element.style.left = '';
        this.element.style.right = '';
        this.element.style.width = '';
        this.element.style.zIndex = '';
        this.hidePlaceholder();
        this.isFixed = false;
    }

    applyFixed() {
        this.showPlaceholder();
        this.element.style.position = 'fixed';
        this.element.style.top = `${this.fixedTopPx}px`;
        this.element.style.left = '0';
        this.element.style.right = '0';
        this.element.style.width = '100%';
        this.element.style.zIndex = '50';
        this.isFixed = true;
    }

    /* ------------------------------------------------------------------ */
    /*  Core update loop                                                   */
    /* ------------------------------------------------------------------ */

    update() {
        this.rafId = null;

        try {
            const isSmall = window.innerWidth < this.options.breakpoint;
            const stuck = this.isStuck();

            if (stuck && !this.isFixed) {
                this.applyFixed();
                this.element.classList.add(this.options.collapsedClass);
            } else if (!stuck && this.isFixed) {
                this.applyStatic();

                if (!isSmall) {
                    this.element.classList.remove(this.options.collapsedClass);
                }
            }
        } catch (err) {
            console.error('StickyBehavior update error:', err);
        }
    }

    /* ------------------------------------------------------------------ */
    /*  Responsive                                                         */
    /* ------------------------------------------------------------------ */

    checkResponsive() {
        try {
            const isSmall = window.innerWidth < this.options.breakpoint;

            if (isSmall) {
                this.element.classList.add(this.options.collapsedClass);
            } else if (!this.isStuck()) {
                this.element.classList.remove(this.options.collapsedClass);
            }
        } catch (err) {
            console.error('StickyBehavior responsive error:', err);
        }
    }

    /* ------------------------------------------------------------------ */
    /*  Event handlers                                                     */
    /* ------------------------------------------------------------------ */

    handleScroll() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => this.update());
    }

    handleResize() {
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

        this.resizeTimeout = setTimeout(() => {
            const wasFixed = this.isFixed;
            if (wasFixed) this.applyStatic();

            this.fixedTopPx = this.getFixedTopPx();
            this.cacheOriginalPosition();
            this.checkResponsive();
            this.update();
        }, this.options.resizeDebounce);
    }

    /* ------------------------------------------------------------------ */
    /*  Cleanup                                                            */
    /* ------------------------------------------------------------------ */

    destroy() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);

        this.applyStatic();
        this.element.classList.remove(this.options.collapsedClass);

        if (this.placeholder && this.placeholder.parentNode) {
            this.placeholder.parentNode.removeChild(this.placeholder);
        }
    }
}
